import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { IconExclamation } from '@components/icons/exclamation';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useDocumentTemplateJourneys } from 'api/documents/hooks';
import { JourneyListItem } from 'ui/authenticated/main/pages/journeys/journey-item';

export type DocumentJourneysTabProps = {
  documentShortId: string;
};

export const DocumentJourneysTab = ({ documentShortId }: DocumentJourneysTabProps) => {
  const { t } = useScopedTranslate(locale);
  const journeysQuery = useDocumentTemplateJourneys(documentShortId);

  if (journeysQuery.isError) {
    return (
      <PageContent>
        <EmptyState
          skipMargin
          icon={<IconExclamation />}
          header={t('loadFailed')}
          content={t('loadFailedDescription')}
          action={
            <Button
              text={t('tryAgain')}
              variant="secondary"
              onClick={() => journeysQuery.refetch()}
            />
          }
        />
      </PageContent>
    );
  }

  if (journeysQuery.isLoading) {
    return <Spinner />;
  }

  const journeys = journeysQuery.data ?? [];

  return (
    <PageContent>
      {journeys.length === 0 ? (
        <EmptyState
          skipMargin
          icon={<IconJourneyItem />}
          header={t('noJourneys')}
          content={t('noJourneysDescription')}
        />
      ) : (
        <Stack space={2}>
          {journeys.map((journey) => {
            return <JourneyListItem key={journey.shortId} journey={journey} hideMenu />;
          })}
        </Stack>
      )}
    </PageContent>
  );
};

const locale = {
  en: {
    noJourneys: 'No journeys',
    noJourneysDescription: 'This document is not used in any journeys yet',
    loadFailed: "Couldn't load journeys",
    loadFailedDescription: 'Something went wrong fetching the journeys using this document.',
    tryAgain: 'Try again',
  },
};
